/**
 * pg-boss queues of the worker (specs/c2-kontratlar.md 4.1 and 4.2). Scheduled queues
 * are named after their config/schedules.yaml job id. Job payloads carry ids and slots,
 * never content; the handler reads everything with its own role.
 *
 * pg-boss schedule() sends the same data on every tick, so a scheduled job carries
 * { trigger: 'schedule' } and the handler computes its slot (cronPrev(createdOn + 1 s)).
 * Only catchup and manual sends carry the slot and singletonKey "<queue>:<slot>".
 */
import { z } from 'zod';
import { approvalStatusSchema, executionStatusSchema, idStringSchema, isoDateTimeSchema } from './ids.js';
export const QUEUE_NAME_PATTERN = /^[a-z][a-z0-9-]{2,63}$/;
/** The pg-boss schema (0001 creates it, owned by twin_migrator). */
export const PGBOSS_SCHEMA = 'pgboss';
/** Queues fed by schedules.yaml (queue name = job id). */
export const SCHEDULED_QUEUES = [
    'ingest-daily',
    'gm-morning-plan',
    'daily-report',
    'weekly-plan',
    'expire-approvals',
    'stage-health',
    'schedule-catchup',
    'retention-sweep',
    'retention-dry-run',
    'audit-partitions',
    'lead-scan',
    'lead-verify',
    'etsy-token-refresh',
    'gmail-poll',
    'sekreter-digest',
    'learn-daily',
    'learn-weekly',
    'outreach-weekly',
    'ai-visibility',
    'api-quota',
    'outreach-hourly',
    'outreach-send',
    'site-log',
    'site-sources',
    'channel-stats',
    /** v0.1.255: texts voiced through Genesis /v1/audio/speech (Seslendirme). */
    'voice-jobs',
    'team-narrator',
    'site-weekly',
    'site-bot-eye',
    'site-crawler-ranges',
    'site-publish',
    'seo-gaps',
    'mail-history',
    'etsy-orders-poll',
    'sent-poll',
    'delivery-check',
    'outreach-replies',
    'eval-temsilci',
    /** v0.1.265: the Pinterest pin queue, hourly 09:00-22:00, without approval cards. */
    'pinterest-publish',
];
/**
 * The pipeline stage each scheduled queue records its runs under (twin.stage_runs). The worker's schedule plan checks
 * it against config; the api reads each job's last run with it (Ofis page, 28 Oct 2026).
 */
export const SCHEDULED_QUEUE_STAGES = Object.freeze({
    'ingest-daily': { pipeline: 'schedule', stage: 'ingest_daily' },
    'gm-morning-plan': { pipeline: 'schedule', stage: 'gm_morning_plan' },
    'daily-report': { pipeline: 'schedule', stage: 'daily_report' },
    'weekly-plan': { pipeline: 'schedule', stage: 'weekly_plan' },
    'expire-approvals': { pipeline: 'schedule', stage: 'expire_approvals' },
    'stage-health': { pipeline: 'ops', stage: 'stage_health' },
    'schedule-catchup': { pipeline: 'schedule', stage: 'catchup' },
    'retention-sweep': { pipeline: 'retention', stage: 'sweep' },
    'retention-dry-run': { pipeline: 'retention', stage: 'sweep' },
    'audit-partitions': { pipeline: 'ops', stage: 'audit_partitions' },
    'lead-scan': { pipeline: 'geo_scan', stage: 'run_queries' },
    'lead-verify': { pipeline: 'lead_enrich', stage: 'referee' },
    'etsy-token-refresh': { pipeline: 'ops', stage: 'etsy_token_refresh' },
    'etsy-orders-poll': { pipeline: 'ops', stage: 'etsy_orders_poll' },
    'gmail-poll': { pipeline: 'ingest_mail', stage: 'poll' },
    'mail-history': { pipeline: 'ingest_mail', stage: 'history' },
    'sent-poll': { pipeline: 'ingest_mail', stage: 'sent_poll' },
    'sekreter-digest': { pipeline: 'ops', stage: 'sekreter_digest' },
    'learn-daily': { pipeline: 'learning', stage: 'arms' },
    'learn-weekly': { pipeline: 'learning', stage: 'vocabulary' },
    'outreach-weekly': { pipeline: 'outreach', stage: 'weekly' },
    'ai-visibility': { pipeline: 'site', stage: 'ai_visibility' },
    'api-quota': { pipeline: 'ops', stage: 'api_quota' },
    'outreach-hourly': { pipeline: 'outreach', stage: 'hourly' },
    'outreach-send': { pipeline: 'outreach', stage: 'send' },
    'site-log': { pipeline: 'site', stage: 'server_log' },
    'site-sources': { pipeline: 'site', stage: 'sources' },
    'channel-stats': { pipeline: 'site', stage: 'channels' },
    'voice-jobs': { pipeline: 'media', stage: 'voice' },
    'team-narrator': { pipeline: 'ops', stage: 'team_narrator' },
    'site-weekly': { pipeline: 'site', stage: 'weekly' },
    'site-bot-eye': { pipeline: 'site', stage: 'bot_eye' },
    'site-crawler-ranges': { pipeline: 'site', stage: 'crawler_ranges' },
    'site-publish': { pipeline: 'site', stage: 'publish' },
    'seo-gaps': { pipeline: 'site', stage: 'seo_gaps' },
    'delivery-check': { pipeline: 'ops', stage: 'delivery_check' },
    'outreach-replies': { pipeline: 'outreach', stage: 'replies' },
    'eval-temsilci': { pipeline: 'learning', stage: 'eval_temsilci' },
    'pinterest-publish': { pipeline: 'site', stage: 'pinterest_publish' },
});
/** Queues fed by events (task dispatcher, propose_action, approval changes). */
export const EVENT_QUEUES = ['agent-task', 'verify-proposal', 'approval-outcome', 'send-reply'];
export const QUEUE_NAMES = [...SCHEDULED_QUEUES, ...EVENT_QUEUES];
const MINUTE = 60;
/**
 * Policies of 4.2. retryDelaySeconds is 5 min for ingest (table) and 60 s where the table
 * gives none. Scheduled queues are stately: a tick waiting in the queue is not queued
 * twice and catchup sends dedupe on "<queue>:<slot>". agent-task and verify-proposal
 * dedupe on task:<id> and approval:<id>. approval-outcome is standard: every status
 * change of an approval is its own job.
 */
export const QUEUE_POLICIES = Object.freeze({
    'ingest-daily': {
        policy: 'stately',
        retryLimit: 2,
        retryDelaySeconds: 5 * MINUTE,
        expireInSeconds: 30 * MINUTE,
        roles: ['app'],
    },
    'gm-morning-plan': {
        policy: 'stately',
        retryLimit: 0,
        retryDelaySeconds: 60,
        expireInSeconds: 20 * MINUTE,
        roles: ['app'],
    },
    'daily-report': {
        policy: 'stately',
        retryLimit: 1,
        retryDelaySeconds: 60,
        expireInSeconds: 15 * MINUTE,
        roles: ['report', 'app'],
    },
    'weekly-plan': {
        policy: 'stately',
        retryLimit: 0,
        retryDelaySeconds: 60,
        expireInSeconds: 30 * MINUTE,
        roles: ['app'],
    },
    'expire-approvals': {
        policy: 'stately',
        retryLimit: 0,
        retryDelaySeconds: 60,
        expireInSeconds: MINUTE,
        roles: ['app'],
    },
    'stage-health': {
        policy: 'stately',
        retryLimit: 0,
        retryDelaySeconds: 60,
        expireInSeconds: 2 * MINUTE,
        roles: ['app'],
    },
    /** Etsy order poll (v0.1.41): one or two receipt reads every 15 minutes, a Telegram line per new sale. */
    'etsy-orders-poll': {
        policy: 'stately',
        retryLimit: 0,
        retryDelaySeconds: 60,
        expireInSeconds: 5 * MINUTE,
        roles: ['app'],
    },
    'etsy-token-refresh': {
        policy: 'stately',
        retryLimit: 0,
        retryDelaySeconds: 60,
        expireInSeconds: 2 * MINUTE,
        roles: ['app'],
    },
    'gmail-poll': {
        policy: 'stately',
        retryLimit: 0,
        retryDelaySeconds: 60,
        expireInSeconds: 2 * MINUTE,
        roles: ['app'],
    },
    /** Past replies from the Sent folder (v0.1.37): up to 300 mails a run over IMAP, no retry. */
    'mail-history': {
        policy: 'stately',
        retryLimit: 0,
        retryDelaySeconds: 600,
        expireInSeconds: 20 * MINUTE,
        roles: ['app'],
    },
    /** Sercan's own replies from the Sent folder (v0.1.50): a handful of mails every five minutes, no retry. */
    'sent-poll': {
        policy: 'stately',
        retryLimit: 0,
        retryDelaySeconds: 60,
        expireInSeconds: 2 * MINUTE,
        roles: ['app'],
    },
    /** v0.1.235: the nightly delivery check (R2 objects of the packages and of the open links); one retry. */
    'delivery-check': {
        policy: 'stately',
        retryLimit: 1,
        retryDelaySeconds: 600,
        expireInSeconds: 5 * MINUTE,
        roles: ['app'],
    },
    'sekreter-digest': {
        policy: 'stately',
        retryLimit: 0,
        retryDelaySeconds: 60,
        expireInSeconds: 2 * MINUTE,
        roles: ['app'],
    },
    'schedule-catchup': {
        policy: 'stately',
        retryLimit: 0,
        retryDelaySeconds: 60,
        expireInSeconds: MINUTE,
        roles: ['app'],
    },
    'retention-sweep': {
        policy: 'stately',
        retryLimit: 1,
        retryDelaySeconds: 60,
        expireInSeconds: 20 * MINUTE,
        roles: ['app'],
    },
    'retention-dry-run': {
        policy: 'stately',
        retryLimit: 0,
        retryDelaySeconds: 60,
        expireInSeconds: 20 * MINUTE,
        roles: ['app'],
    },
    'audit-partitions': {
        policy: 'stately',
        retryLimit: 2,
        retryDelaySeconds: 60,
        expireInSeconds: 5 * MINUTE,
        roles: ['app'],
    },
    'lead-scan': {
        policy: 'stately',
        retryLimit: 0,
        retryDelaySeconds: 60,
        expireInSeconds: 60 * MINUTE,
        roles: ['outreach'],
    },
    'lead-verify': {
        policy: 'stately',
        retryLimit: 0,
        retryDelaySeconds: 60,
        expireInSeconds: 60 * MINUTE,
        roles: ['outreach'],
    },
    /** Arm statistics from the scan's own records (v0.1.34); nothing external, one retry. */
    'learn-daily': {
        policy: 'stately',
        retryLimit: 1,
        retryDelaySeconds: 300,
        expireInSeconds: 20 * MINUTE,
        roles: ['outreach'],
    },
    /** Keyword candidates from stored pages and the weekly learning batch (v0.1.35); nothing external. */
    'learn-weekly': {
        policy: 'stately',
        retryLimit: 1,
        retryDelaySeconds: 600,
        expireInSeconds: 20 * MINUTE,
        roles: ['outreach'],
    },
    /** The weekly outreach summary on Telegram (v0.1.144); read only, one retry. */
    'outreach-weekly': {
        policy: 'stately',
        retryLimit: 1,
        retryDelaySeconds: 600,
        expireInSeconds: 10 * MINUTE,
        roles: ['outreach'],
    },
    /** The weekly AI visibility check (v0.1.160): about 60 outside calls, one retry picks up what failed. */
    'ai-visibility': {
        policy: 'stately',
        retryLimit: 1,
        retryDelaySeconds: 1800,
        expireInSeconds: 20 * MINUTE,
        roles: ['app'],
    },
    /** Outreach steps (v0.1.168): drafts, address checks, wave audit, inbox test and sender, every hour; a missed hour waits. */
    'outreach-hourly': {
        policy: 'stately',
        retryLimit: 0,
        retryDelaySeconds: 60,
        expireInSeconds: 15 * MINUTE,
        roles: ['outreach'],
    },
    /** Outreach sender (v0.1.228): every ten minutes, at most one mail, spread at random over the business hours. */
    'outreach-send': {
        policy: 'stately',
        retryLimit: 0,
        retryDelaySeconds: 60,
        expireInSeconds: 5 * MINUTE,
        roles: ['outreach'],
    },
    /** API kalanları (v0.1.164): three small balance reads every three hours; a missed read waits for the next tick. */
    'api-quota': {
        policy: 'stately',
        retryLimit: 0,
        retryDelaySeconds: 60,
        expireInSeconds: 2 * MINUTE,
        roles: ['app'],
    },
    /** Site statistics (v0.1.166): the access log every hour; a missed hour is read with the next one (offsets). */
    'site-log': {
        policy: 'stately',
        retryLimit: 0,
        retryDelaySeconds: 60,
        expireInSeconds: 15 * MINUTE,
        roles: ['app'],
    },
    /** GA4, Search Console and Bing Webmaster once a day; one retry later picks up a source that failed. */
    'site-sources': {
        policy: 'stately',
        retryLimit: 1,
        retryDelaySeconds: 1800,
        expireInSeconds: 10 * MINUTE,
        roles: ['app'],
    },
    /** Kanallar (28 Oct 2026): YouTube, Pinterest and CGTrader once a day; one retry later picks up a channel that failed. */
    'channel-stats': {
        policy: 'stately',
        retryLimit: 1,
        retryDelaySeconds: 1800,
        expireInSeconds: 10 * MINUTE,
        roles: ['app'],
    },
    /**
     * Seslendirme (v0.1.255): every 5 minutes the oldest open voice job; the next tick goes on where one stopped. A tick
     * starts no part after 3 minutes and a part takes at most 2, so 12 minutes is well past its end (review of #145).
     */
    'voice-jobs': {
        policy: 'stately',
        retryLimit: 0,
        retryDelaySeconds: 60,
        expireInSeconds: 12 * MINUTE,
        roles: ['app'],
    },
    /** Ekip kanalı narrator (28 Oct 2026): chat lines from the lead flow every 15 minutes; the next tick is the retry. */
    'team-narrator': {
        policy: 'stately',
        retryLimit: 0,
        retryDelaySeconds: 60,
        expireInSeconds: 5 * MINUTE,
        roles: ['app'],
    },
    /** v0.1.192: the queue folder of the site builder every two minutes; the next tick is the retry. */
    'site-publish': {
        policy: 'stately',
        retryLimit: 0,
        retryDelaySeconds: 60,
        expireInSeconds: 5 * MINUTE,
        roles: ['app'],
    },
    /**
     * v0.1.235: the replies to the outreach mails every five minutes, out of gmail-poll (they kept it past its two-minute
     * limit); the next tick is the retry.
     */
    'outreach-replies': {
        policy: 'stately',
        retryLimit: 0,
        retryDelaySeconds: 60,
        expireInSeconds: 4 * MINUTE,
        roles: ['app', 'outreach'],
    },
    /** v0.1.260: the Temsilci's reply evaluation (about 18 drafts); a failed run waits for the next week or a request. */
    'eval-temsilci': {
        policy: 'stately',
        retryLimit: 0,
        retryDelaySeconds: 600,
        expireInSeconds: 30 * MINUTE,
        roles: ['app'],
    },
    /**
     * v0.1.265: the Pinterest pin queue every hour 09:00-22:00, at most two pins a run with a board look-up each; the
     * next hour is the retry (no pg-boss retry: a pin Pinterest made must not be sent twice).
     */
    'pinterest-publish': {
        policy: 'stately',
        retryLimit: 0,
        retryDelaySeconds: 60,
        expireInSeconds: 10 * MINUTE,
        roles: ['app'],
    },
    /** v0.1.192: the SEO loop's weekly gaps (Monday, after the AI check); one retry. */
    'seo-gaps': {
        policy: 'stately',
        retryLimit: 1,
        retryDelaySeconds: 600,
        expireInSeconds: 5 * MINUTE,
        roles: ['app'],
    },
    /**
     * Bot gözü testi (26 Oct 2026): the important pages as the crawlers see them, Monday 07:00. At most 149 requests two
     * seconds apart with 20 seconds each: an hour covers the worst case. No retry: a second run asks the site again.
     */
    'site-bot-eye': {
        policy: 'stately',
        retryLimit: 0,
        retryDelaySeconds: 600,
        expireInSeconds: 60 * MINUTE,
        roles: ['app'],
    },
    /** Crawler address verification (26 Oct 2026): the published range files once a day; one retry half an hour later. */
    'site-crawler-ranges': {
        policy: 'stately',
        retryLimit: 1,
        retryDelaySeconds: 1800,
        expireInSeconds: 10 * MINUTE,
        roles: ['app'],
    },
    /** The weekly site summary on Telegram, and (v0.1.211) the list of pages that went out; one retry. */
    'site-weekly': {
        policy: 'stately',
        retryLimit: 1,
        retryDelaySeconds: 600,
        expireInSeconds: 10 * MINUTE,
        roles: ['app'],
    },
    'agent-task': {
        policy: 'stately',
        retryLimit: 0,
        retryDelaySeconds: 60,
        expireInSeconds: 20 * MINUTE,
        roles: ['app'],
    },
    /** One retry, for connection errors only. */
    'verify-proposal': {
        policy: 'stately',
        retryLimit: 1,
        retryDelaySeconds: 60,
        expireInSeconds: 5 * MINUTE,
        roles: ['app', 'outreach'],
    },
    'approval-outcome': {
        policy: 'standard',
        retryLimit: 2,
        retryDelaySeconds: 60,
        expireInSeconds: 2 * MINUTE,
        roles: ['app'],
    },
    /** Gönderilecekler (v0.1.54): one SMTP send per row; a failure is recorded on the row, never retried blindly. */
    'send-reply': {
        policy: 'standard',
        retryLimit: 0,
        retryDelaySeconds: 60,
        expireInSeconds: 2 * MINUTE,
        roles: ['app'],
    },
});
/** When the GM is busy (no model call was made) the job goes back to the queue after this delay. */
export const GM_BUSY_RETRY_SECONDS = 2 * MINUTE;
/** Local concurrency of agent-task: one GM and one Genesis job. */
export const AGENT_TASK_CONCURRENCY = 2;
/** Advisory lock key of the single GM run (Postgres hashtext of this text). */
export const GM_RUN_LOCK_NAME = 'twin.gm';
export const scheduleTriggerSchema = z.enum(['schedule', 'catchup', 'manual']);
/** Payload of every scheduled queue. slot is absent for schedule and required for catchup and manual. */
export const scheduledJobPayloadSchema = z
    .strictObject({ trigger: scheduleTriggerSchema, slot: isoDateTimeSchema.optional() })
    .superRefine((payload, ctx) => {
    if (payload.trigger === 'schedule' && payload.slot !== undefined) {
        ctx.addIssue({
            code: 'custom',
            path: ['slot'],
            message: 'a scheduled tick carries no slot; the handler computes it',
        });
    }
    if (payload.trigger !== 'schedule' && payload.slot === undefined) {
        ctx.addIssue({ code: 'custom', path: ['slot'], message: 'catchup and manual sends carry their slot' });
    }
});
export const agentTaskPayloadSchema = z.strictObject({ taskId: idStringSchema });
export const verifyProposalPayloadSchema = z.strictObject({ approvalId: idStringSchema });
export const approvalOutcomePayloadSchema = z.strictObject({
    approvalId: idStringSchema,
    status: approvalStatusSchema,
    executionStatus: executionStatusSchema,
});
/** send-reply (v0.1.54): the outbox row Sercan queued with Gönder. */
export const sendReplyPayloadSchema = z.strictObject({ outboxId: idStringSchema });
export const EVENT_PAYLOAD_SCHEMAS = Object.freeze({
    'agent-task': agentTaskPayloadSchema,
    'verify-proposal': verifyProposalPayloadSchema,
    'approval-outcome': approvalOutcomePayloadSchema,
    'send-reply': sendReplyPayloadSchema,
});
/** The payload schema of any queue. */
export function payloadSchemaFor(queue) {
    return EVENT_QUEUES.includes(queue)
        ? EVENT_PAYLOAD_SCHEMAS[queue]
        : scheduledJobPayloadSchema;
}
/** singletonKey helpers. */
export const singletonKeys = Object.freeze({
    scheduled: (queue, slot) => `${queue}:${slot}`,
    task: (taskId) => `task:${taskId}`,
    approval: (approvalId) => `approval:${approvalId}`,
});
//# sourceMappingURL=queues.js.map